import { usePostHog } from "posthog-js/react";
import type React from "react";
import { useCallback } from "react";
import { twJoin } from "tailwind-merge";

import type { AssistantConversationListItemDto } from "@/api/types";
import { ContextMenu } from "@/components/ContextMenu/ContextMenu";
import { ChevronRightIcon } from "@/components/Icons/ChevronRightIcon";
import { MessageChatSquareIcon } from "@/components/Icons/MessageChatSquareIcon";
import { Trash02Icon } from "@/components/Icons/Trash02Icon";
import { Button, buttonVariants } from "@/design-system/Button";
import { IconButton } from "@/design-system/IconButton";
import { Spinner } from "@/design-system/Spinner";
import { twResolve } from "@/helpers/tw-resolve";
import { useOnIntersection } from "@/hooks/useOnIntersection";
import { useTranslation } from "@/translations";

interface AskNobiChatSidebarProps {
  hasChatted: boolean;
  conversations: AssistantConversationListItemDto[];
  activeConversationId: string | undefined;
  loadingConversationId: string | undefined;
  onNewConversation: () => void;
  onSelectConversation: (conversationId: string) => void;
  onDeleteConversation: (conversation: AssistantConversationListItemDto) => void;
  isDeleteConversationDisabled: (conversationId: string) => boolean;
  isDesktopExpanded: boolean;
  onToggleDesktopExpanded: () => void;
  isMobileOpen: boolean;
  onCloseMobile: () => void;
  hasMoreConversations: boolean;
  isFetchingMoreConversations: boolean;
  onLoadMoreConversations: () => void;
}

export function AskNobiChatSidebar({
  hasChatted,
  conversations,
  activeConversationId,
  loadingConversationId,
  isDesktopExpanded,
  onToggleDesktopExpanded,
  isMobileOpen,
  onCloseMobile,
  onNewConversation,
  onSelectConversation,
  onDeleteConversation,
  isDeleteConversationDisabled,
  hasMoreConversations,
  isFetchingMoreConversations,
  onLoadMoreConversations,
}: AskNobiChatSidebarProps): React.ReactNode {
  const { t } = useTranslation();
  const postHog = usePostHog();

  const loadMoreRef = useOnIntersection({
    onIntersect: useCallback(() => {
      if (!isFetchingMoreConversations && hasMoreConversations) {
        onLoadMoreConversations();
      }
    }, [isFetchingMoreConversations, hasMoreConversations, onLoadMoreConversations]),
  });

  const hasContent = conversations.length > 0 || hasChatted;

  if (!hasContent) {
    return null;
  }

  const renderSidebarContent = (expanded: boolean, showToggle = false, desktopOnly = false) => (
    <aside
      className={twJoin(
        "relative h-full shrink-0 flex-col border-r-hairline border-border-default bg-background-default pt-space-sm @md:bg-transparent",
        desktopOnly ? "hidden @md:flex" : "flex",
        expanded ? "w-select-responsive-filter" : "w-flow-header",
        "transition-all duration-200",
      )}
    >
      <header className={expanded ? "px-card-compact" : "pt-space-sm pl-space-sm"}>
        {expanded ? (
          <Button
            variant="subtle"
            size="lg"
            fullWidth
            onClick={onNewConversation}
            disabled={loadingConversationId !== undefined || activeConversationId === undefined}
          >
            {t("page.ask-nobi-chat.conversation-sidebar.new-conversation")}
          </Button>
        ) : (
          <IconButton
            variant="ghostPrimary"
            onClick={onNewConversation}
            icon={<MessageChatSquareIcon />}
            title={t("page.ask-nobi-chat.conversation-sidebar.new-conversation")}
            disabled={loadingConversationId !== undefined || activeConversationId === undefined}
          />
        )}
      </header>

      {expanded && (
        <>
          <h2
            id="ask-nobi-recent-conversations"
            className="mx-space-sm mt-nobi-sidebar-section flex h-control-sm items-center px-card-compact text-overline text-text-secondary"
          >
            {t("page.ask-nobi-chat.conversation-sidebar.recent")}
          </h2>

          <ul
            aria-labelledby="ask-nobi-recent-conversations"
            className="flex min-h-0 flex-1 scrollbar-transparent flex-col gap-space-xs overflow-y-auto px-space-sm pb-card-compact"
          >
            {conversations.map((conversation) => {
              const isActive = conversation.id === activeConversationId;
              const isLoading = conversation.id === loadingConversationId;

              return (
                <li
                  key={conversation.id}
                  className={twJoin(
                    "flex shrink-0 items-center rounded-lg transition-colors",
                    (loadingConversationId ? conversation.id === loadingConversationId : isActive)
                      ? "bg-background-secondary text-caption-emphasis text-text-primary"
                      : "text-text-secondary hover:bg-background-secondary",
                  )}
                >
                  <button
                    type="button"
                    disabled={isActive || isLoading}
                    onClick={() => {
                      postHog.capture("clicked_ask_nobi_history_item", { page: "ask_nobi_chat_sidebar" });
                      onSelectConversation(conversation.id);
                    }}
                    className={twResolve(
                      buttonVariants({ variant: "subtle", size: "sm" }),
                      "h-auto min-w-0 flex-1 justify-start truncate rounded-lg p-card-compact text-left text-caption",
                    )}
                    title={conversation.title}
                  >
                    {conversation.title}
                  </button>
                  {conversation.canDelete && (
                    <ContextMenu
                      items={[
                        {
                          labels: { default: t("page.ask-nobi-chat.conversation-sidebar.delete.action") },
                          value: "delete",
                          icon: <Trash02Icon />,
                          callbackFn: () => onDeleteConversation(conversation),
                          isDisabled: isDeleteConversationDisabled(conversation.id),
                          "data-testid": `delete-nobi-conversation-${conversation.id}`,
                        },
                      ]}
                    />
                  )}
                </li>
              );
            })}

            {hasMoreConversations && (
              <li ref={loadMoreRef} className="flex justify-center py-space-sm">
                <Spinner size="xs" />
              </li>
            )}
          </ul>
        </>
      )}

      {showToggle && (
        <div
          onClick={onToggleDesktopExpanded}
          className="group/sidebar-collapse-trigger absolute top-0 -right-2 z-10 h-full cursor-ew-resize px-space-sm"
        >
          <div className="absolute top-7 left-1/2 z-20 -translate-1/2">
            <IconButton
              size="xs"
              variant="soft"
              title={expanded ? "Collapse sidebar" : "Expand sidebar"}
              icon={
                <span className={twResolve("block transition-transform", expanded && "rotate-180")}>
                  <ChevronRightIcon />
                </span>
              }
              isCircular
              withTooltip={false}
            />
          </div>
        </div>
      )}
    </aside>
  );

  return (
    <>
      {/* Desktop sidebar */}
      {renderSidebarContent(isDesktopExpanded, true, true)}

      {/* Mobile overlay */}
      {isMobileOpen && (
        <div className="absolute inset-0 z-10 flex @md:hidden">
          <div className="animate-in duration-200 slide-in-from-left">{renderSidebarContent(true)}</div>
          <button
            type="button"
            className={twResolve(
              buttonVariants({ variant: "ghostSecondary", size: "sm" }),
              "h-auto flex-1 rounded-none bg-background-default-inverse/20 p-0",
            )}
            onClick={onCloseMobile}
            aria-label={t("common.action.close")}
          />
        </div>
      )}
    </>
  );
}
